/**
 * AYEKA · Oferta única — helpers de URL/UTMs.
 *
 * Fuente: growth/oferta_unica/COPY_OFERTA_UNICA_v2_sin_fundadores_2026-10-25.md §1.
 * No hay cupones internos en la oferta única: cualquier `cupon` / `coupon` del query se descarta.
 * `promo` es un código de promoción de Stripe (campaña AYEKA25). Se conserva y se guarda
 * en sessionStorage para el checkout de /ayeka/continuar. No es un cupón interno.
 */

const COUPON_KEYS = ['cupon', 'coupon'];
const PROMO_STORAGE_KEY = 'ayeka_stripe_promo';
const PROMO_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/;

/** Lee `?promo=` (si viene) y lo deja en sessionStorage. Devuelve el código guardado o ''. */
export function rememberPromoFromSearch(search = '') {
  const code = (new URLSearchParams(search || '').get('promo') || '').trim();
  if (code && PROMO_RE.test(code)) {
    try { sessionStorage.setItem(PROMO_STORAGE_KEY, code); } catch { /* noop */ }
  }
  return readStoredPromo();
}

/** Código de promoción de Stripe guardado en esta pestaña, o ''. */
export function readStoredPromo() {
  try {
    const code = (sessionStorage.getItem(PROMO_STORAGE_KEY) || '').trim();
    return PROMO_RE.test(code) ? code : '';
  } catch {
    return '';
  }
}

/** Query string sin cupon/coupon. Devuelve '' o '?…'. `promo` se conserva. */
export function stripCouponParams(search = '') {
  const params = new URLSearchParams(search || '');
  COUPON_KEYS.forEach((k) => params.delete(k));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** utm_* presentes en un query string, como objeto. */
export function utmsFromSearch(search = '') {
  const params = new URLSearchParams(search || '');
  const out = {};
  params.forEach((v, k) => {
    if (k.startsWith('utm_') && v && v.trim()) out[k] = v.trim();
  });
  return out;
}

/**
 * Construye `pathname?query` para un redirect:
 *  - quita cupon/coupon;
 *  - conserva lo demás que venga (utm_*, fbclid, …);
 *  - si NO viene ninguna utm_*, agrega el juego completo `defaultUtms`.
 *    (No se mezclan: un utm_source entrante con un utm_medium por defecto
 *    daría una atribución inventada.)
 */
export function redirectUrlWithUtms(pathname, search = '', defaultUtms = {}) {
  const params = new URLSearchParams(stripCouponParams(search));
  const hasUtms = Object.keys(utmsFromSearch(params.toString())).length > 0;
  if (!hasUtms) {
    Object.entries(defaultUtms).forEach(([k, v]) => params.set(k, v));
  }
  const s = params.toString();
  return `${pathname}${s ? `?${s}` : ''}`;
}

// UTMs por defecto de la tabla de redirects (§1).
export const REDIRECT_UTMS = {
  ayeka: {
    utm_source: 'direct', utm_medium: 'redirect',
    utm_campaign: 'ayeka_oferta_unica', utm_content: 'ayeka_corto',
  },
  rutaCritica: {
    utm_source: 'direct', utm_medium: 'redirect',
    utm_campaign: 'ruta_critica', utm_content: 'ruta_critica_corto',
  },
  ayekaPromo: {
    utm_source: 'legacy', utm_medium: 'redirect',
    utm_campaign: 'ayeka_oferta_unica', utm_content: 'ayeka_promo',
  },
  promoCode: {
    utm_source: 'legacy', utm_medium: 'redirect',
    utm_campaign: 'ayeka_oferta_unica', utm_content: 'promo_code',
  },
  // /links (link de la bio de Instagram) ya no es página: redirect a la oferta única
  // (Jack, 25 oct 2026). Conserva la atribución que tenía el botón destacado.
  links: {
    utm_source: 'instagram', utm_medium: 'bio',
    utm_campaign: 'ayeka_oferta_unica', utm_content: 'links_boton',
  },
};

// Botón "Reactivar" (banner, perfil y AYEKA vencida): checkout V2 $490/$4,990 (Jack, 25 oct 2026).
export const REACTIVAR_PATH = '/ayeka/continuar';
export const reactivarUrl = (content) =>
  `${REACTIVAR_PATH}?utm_source=app&utm_medium=reactivar&utm_campaign=ayeka_oferta_unica&utm_content=${encodeURIComponent(content)}`;
